// PartBPage1.jsx - Secondary Stage Part B Page 1 (Project Details) for HPC Report
import React from 'react';

const PartBPage1 = ({ data }) => {
  // Page 1 has both teacher_fields and student_fields
  const teacherFields = data?.teacher_fields || {};
  const studentFields = data?.student_fields || {};

  const guidingQuestions = studentFields.guiding_questions || [];
  const whatIKnow = studentFields.what_i_know || '';
  const whatINeedToFindOut = studentFields.what_i_need_to_find_out || '';

  const pedagogyOptions = [
    { key: 'art_integrated', label: 'Art-integrated' },
    { key: 'sports_integrated', label: 'Sports-integrated' },
    { key: 'toy_based', label: 'Toy-based' },
    { key: 'technology_integrated', label: 'Technology-integrated' },
    { key: 'skill_based_learning', label: 'Skill-based learning' },
    { key: 'drama_theatre_integrated', label: 'Drama/Theatre-integrated' },
    { key: 'indian_knowledge_systems', label: 'Indian Knowledge Systems approaches' }
  ];

  const renderText = (text, isMultiline = false) => {
    if (!text || text.trim() === '') return <div style={{ fontSize: '9px', color: '#999' }}>-</div>;
    if (isMultiline) {
      return <div style={{ fontSize: '9px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{text}</div>;
    }
    return <div style={{ fontSize: '9px', lineHeight: '1.5' }}>{text}</div>;
  };

  const renderList = (items) => {
    if (!items || items.length === 0) return <div style={{ fontSize: '9px', color: '#999' }}>-</div>;
    return (
      <div style={{ fontSize: '9px', lineHeight: '1.6' }}>
        {items.map((item, index) => (
          <div key={index} style={{ marginBottom: '3px' }}>• {item}</div>
        ))}
      </div>
    );
  };

  return (
    <div className="report-page" style={{
      width: '210mm',
      minHeight: '297mm',
      padding: '20mm',
      backgroundColor: 'white',
      boxSizing: 'border-box',
      fontFamily: 'Arial, sans-serif',
      position: 'relative'
    }}>
      {/* Decorative Corners */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '0 80px 80px 0',
        borderColor: 'transparent #F4824C transparent transparent'
      }} />

      {/* Header */}
      <div style={{
        fontSize: '18px',
        fontWeight: 'bold',
        marginBottom: '8px',
        textAlign: 'center',
        color: '#F4824C'
      }}>
        PART B
      </div>

      <div style={{
        backgroundColor: '#F4824C',
        color: 'white',
        fontSize: '13px',
        fontWeight: 'bold',
        padding: '8px',
        textAlign: 'center',
        marginBottom: '12px'
      }}>
        Group Project Work
      </div>

      {/* Subject(s) */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{
          backgroundColor: '#F4824C',
          color: 'white',
          padding: '6px 12px',
          fontWeight: 'bold',
          fontSize: '10px',
          borderRadius: '20px',
          display: 'inline-block',
          marginBottom: '6px'
        }}>
          Subject(s)<br />(Can be more than one)
        </div>
        <div style={{
          backgroundColor: '#FFE5D5',
          padding: '8px 12px',
          minHeight: '30px',
          borderRadius: '4px'
        }}>
          {renderList(teacherFields.subjects)}
        </div>
      </div>

      {/* Curricular goal(s) */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{
          backgroundColor: '#F4824C',
          color: 'white',
          padding: '6px 12px',
          fontWeight: 'bold',
          fontSize: '10px',
          borderRadius: '20px',
          display: 'inline-block',
          marginBottom: '6px'
        }}>
          Curricular goal(s)<br />(Can be more than one)
        </div>
        <div style={{
          backgroundColor: '#FFE5D5',
          padding: '8px 12px',
          minHeight: '30px',
          borderRadius: '4px'
        }}>
          {renderList(teacherFields.curricular_goals)}
        </div>
      </div>

      {/* Competency(-ies) */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{
          backgroundColor: '#F4824C',
          color: 'white',
          padding: '6px 12px',
          fontWeight: 'bold',
          fontSize: '10px',
          borderRadius: '20px',
          display: 'inline-block',
          marginBottom: '6px'
        }}>
          Competency(-ies)<br />(Can be more than one)
        </div>
        <div style={{
          backgroundColor: '#FFE5D5',
          padding: '8px 12px',
          minHeight: '30px',
          borderRadius: '4px'
        }}>
          {renderList(teacherFields.competencies)}
        </div>
      </div>

      {/* Pedagogies */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{
            backgroundColor: '#F4824C',
            color: 'white',
            padding: '10px 14px',
            fontWeight: 'bold',
            fontSize: '10px',
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minWidth: '120px'
          }}>
            Pedagogies<br />(Tick (✓) all that apply.)
          </div>
          <div style={{
            backgroundColor: '#FFE5D5',
            padding: '10px 14px',
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '6px 16px',
            fontSize: '9px'
          }}>
            {pedagogyOptions.map(option => (
              <div key={option.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '13px',
                  height: '13px',
                  border: '1.5px solid #333',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  flexShrink: 0
                }}>
                  {teacherFields.pedagogies?.[option.key] && '✓'}
                </div>
                <span>{option.label}</span>
              </div>
            ))}
            <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
              <div style={{
                width: '13px',
                height: '13px',
                border: '1.5px solid #333',
                flexShrink: 0
              }}>
                {teacherFields.pedagogies?.any_other && '✓'}
              </div>
              <span>Any other {teacherFields.pedagogies?.any_other && `: ${teacherFields.pedagogies.any_other}`}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Project prompt */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{
          backgroundColor: '#F4824C',
          color: 'white',
          padding: '8px 12px',
          fontWeight: 'bold',
          fontSize: '10px',
          marginBottom: '6px',
          borderTopLeftRadius: '15px',
          borderTopRightRadius: '15px'
        }}>
          Project prompt/question/problem/challenge/<br />planned final output
        </div>
        <div style={{
          backgroundColor: '#FFE5D5',
          padding: '10px 12px',
          minHeight: '40px',
          lineHeight: '1.5'
        }}>
          {renderText(teacherFields.project_prompt, true)}
        </div>
      </div>

      {/* Guiding questions */}
      <div style={{ marginBottom: '10px' }}>
        <div style={{
          backgroundColor: '#4A90A4',
          color: 'white',
          padding: '10px 14px',
          fontWeight: 'bold',
          fontSize: '10px',
          marginBottom: '6px',
          borderTopLeftRadius: '15px',
          borderTopRightRadius: '15px',
          position: 'relative'
        }}>
          Guiding questions
          <div style={{
            position: 'absolute',
            left: '100px',
            top: '100%',
            width: 0,
            height: 0,
            marginLeft: '-10px',
            marginTop: '-5px'
          }}>
            {/* Arrow decoration removed as requested */}
          </div>
        </div>
        <div style={{
          backgroundColor: '#C5E3ED',
          padding: '10px 12px',
          minHeight: '60px'
        }}>
          {renderList(guidingQuestions)}
        </div>
      </div>

      {/* What do I know? / What do I need to find out? */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '15px' }}>
        <div>
          <div style={{
            backgroundColor: '#4A90A4',
            color: 'white',
            padding: '8px 12px',
            fontWeight: 'bold',
            fontSize: '10px',
            textAlign: 'center',
            marginBottom: '6px'
          }}>
            What do I know?
          </div>
          <div style={{
            backgroundColor: '#C5E3ED',
            padding: '10px 12px',
            minHeight: '100px',
            lineHeight: '1.5'
          }}>
            {renderText(whatIKnow, true)}
          </div>
        </div>

        <div>
          <div style={{
            backgroundColor: '#4A90A4',
            color: 'white',
            padding: '8px 12px',
            fontWeight: 'bold',
            fontSize: '10px',
            textAlign: 'center',
            marginBottom: '6px'
          }}>
            What do I need to find out?
          </div>
          <div style={{
            backgroundColor: '#C5E3ED',
            padding: '10px 12px',
            minHeight: '100px',
            lineHeight: '1.5'
          }}>
            {renderText(whatINeedToFindOut, true)}
          </div>
        </div>
      </div>

      {/* Page Number */}
      <div style={{
        position: 'absolute',
        bottom: '15mm',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '10px',
        color: '#666'
      }}>
        11
      </div>

      {/* Bottom decorative corners */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '60px 0 0 60px',
        borderColor: 'transparent transparent transparent #F4824C'
      }} />
      <div style={{
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '60px 60px 0 0',
        borderColor: 'transparent #F4824C transparent transparent',
        opacity: 0.3
      }} />
    </div>
  );
};

export default PartBPage1;
